import { catchError, forkJoin, of, switchMap, tap, filter, map, distinctUntilChanged } from 'rxjs';
import { ofType } from 'redux-observable';
import messageService from 'layout/services/messageService';
import uiStateSlice from 'layout/redux/uiStateSlice';
import LoadingStatus from 'enums/loadingStatus';
import { ResultType, searchParametersActions, selectResultType } from 'features/searchResultsFacets';
import { selectIsMarkerAutoScrollFeatureEnabled, selectLayoutBreakpoint } from 'layout/redux/selectors';
import LayoutBreakpoint from 'layout/enums/layoutBreakpoint';
import getMarkersLoaderEndpoints from '../services/markersEndpointSelector';
import { mapStateSlice, interactiveSitePlanSlice } from './slices';
import { 
  geoCoordinates,
  mapMarkers,
  selectedLotSummaryCard,
  selectedNearbyListingCarousel,
  selectedNearbyListingsCarouselStatus,
} from './selectors';
import getCommunityNearbyListings from '../apis/getCommunityNearbyListings';
import getBuildablePlanNearbyListings from '../apis/getBuildablePlanNearbyListings';
import getPlanNearbyListings from '../apis/getPlanNearbyListings';
import getSpecNearbyListings from '../apis/getSpecNearbyListings';
import findNearbyPins from '../services/geoLocationService';
import MarkerType from '../enums/markerType';
import getHousingUnitSummaryCardInformation from '../apis/getHousingUnitSummaryCard';
import getCommunitySummaryCard from '../apis/getCommunitySummaryCard';

const actions = {
  ...mapStateSlice.actions,
  ...uiStateSlice.actions,
  ...interactiveSitePlanSlice.actions,
};

export default (action$) =>
action$.pipe(
  ofType(actions.fetchResults.type),
  switchMap((action) => {
    const apiRequests = getMarkersLoaderEndpoints(action.payload.parameters);
    return forkJoin(apiRequests).pipe(
      switchMap((data) => of(actions.fetchResultsFulfilled({ parameters: action.payload.parameters, ...data }))),
      tap((data) => {
        messageService.sendMessage({
          messageType: 'mapBoundaries',
          payload: {
            mapBoundaries: data.payload.communities?.MapBoundaries || data.payload.homes?.MapBoundaries,
          },
        });
      }),
      catchError((err) => of(actions.fetchResultsError(err)))
    );
  })
);
  
/*
 * Epic to refresh the page number of each marker when the sort event o pagesize event is raised. 
 * Is those events ths listing is refreshed, but the marker not.
 */
export const updateMarkersPageNumberEpic = (action$, state$) =>
  action$.pipe(
    ofType(
      searchParametersActions.fetchSort.type,
      searchParametersActions.fetchPageSize.type,
    ),
    // When the Layout is small the page numbers shouldn't be updated 
    filter(() => selectLayoutBreakpoint(state$.value).getValue() !== LayoutBreakpoint.Small.getValue()),
    switchMap(() => {
      const parameters = state$.value.searchParameters;
      const apiRequests = getMarkersLoaderEndpoints(parameters);
      return forkJoin(apiRequests).pipe(
        switchMap((data) => of(actions.fetchResultsPagesNumbers({ parameters, ...data }))),
      );
    })
  );

/**
 * Epic that clears the summary card when the search parameters change, 
 * the search within map is disabled, or the search within map is enabled.
 * @param {Observable} action$ - Observable stream of actions.
 * @returns {Observable} - Observable stream of actions.
 */
export const clearNearbyListingsCarouselOnMapChangesEpic = (action$) =>
  action$.pipe(
    ofType(
      actions.disableSearchWithinMap.type,
      actions.enableSearchWithinMap.type
    ),
    map(selectedNearbyListingCarousel),
    filter((carousel) => Boolean(carousel?.Id)),
    switchMap(() => of(actions.clearNearbyListingsCarousel()))
  );

  /**
 * Epic for retrieving additional information for a community marker.
 * @param {Observable} action$ - Observable stream of actions.
 * @returns {Observable} - Observable stream of actions.
 */
export const getCommunityNearbyListingsEpic = (action$, state$) =>
  action$.pipe(
    ofType(actions.selectedMarker.type),
    filter(() => {
      const IsMarkerAutoScrollFeatureEnabled = selectIsMarkerAutoScrollFeatureEnabled(state$.value);

      // if auto scroll feature flag is off, then go ahead and fetch the summary card
      if (!IsMarkerAutoScrollFeatureEnabled) {
        return true;
      }

      // since auto scroll feature flag is on, we have to calculate if we have to show the summary card.
      // we fetch the summary card for:
      //    1. every community in small size devices either in /communities/ or /homes/
      //    2. buildable plans in /homes/.
      const layoutBreakpoint = selectLayoutBreakpoint(state$.value);
      const resultType = selectResultType(state$.value);

      return resultType === ResultType.Homes.toString() ||
        layoutBreakpoint.text === LayoutBreakpoint.Small.toString();
    }),
    filter((action) => 
      action.payload?.type?.toLowerCase() === MarkerType.HousingDevelopment.toString()
    ),
    switchMap((action) => {      
      // get all the markers from the state, then filter the nearby pins by a 25 mile radius.
      const allMarkers = mapMarkers(state$.value).filter((pin) => 
        pin.type === MarkerType.HousingDevelopment.toString());
      const coordinates = geoCoordinates(state$.value);
      // Communities is only displayed on smaller devices, so we only want to show 7 results
      const pagination = 7; 
      const nearbyPins = findNearbyPins(action.payload, allMarkers, coordinates, pagination);

      // the backend expects a list of community ids to fetch, so we grab just the IDs
      const ids = nearbyPins.map((pin) => pin.id).join(','); 
      return getCommunityNearbyListings({communityIds: ids}).pipe(
        switchMap((data) => of(actions.getNearbyListingsCarouselFulfilled(data))),
        catchError((error) => of(actions.getNearbyListingsCarouselFailed(error)))
      );
    }));

  /**
 * Epic for retrieving additional information for a community marker.
 * @param {Observable} action$ - Observable stream of actions.
 * @returns {Observable} - Observable stream of actions.
 */
export const getBuildablePlanNearbyListingsEpic = (action$, state$) =>
  action$.pipe(
    ofType(actions.selectedMarker.type),
    filter((action) => 
      action.payload?.type === MarkerType.BuildablePlan.toString()
    ),
    switchMap((action) => {
      const id = action.payload?.id;
      const layoutBreakpoint = selectLayoutBreakpoint(state$.value).getValue();
      const showPlanCards = layoutBreakpoint === LayoutBreakpoint.Small.getValue();

      return getBuildablePlanNearbyListings({
        id,
        showPlanCards
      }).pipe(
        switchMap((data) => of(actions.getNearbyListingsCarouselFulfilled(data))),
        catchError((error) => of(actions.getNearbyListingsCarouselFailed(error)))
      )
    })
  );

/**
 * Epic for retrieving additional marker information for a plan marker.
 *
 * @param {Observable} action$ - The stream of actions.
 * @returns {Observable} Observable stream of actions
 */
export const getPlanNearbyListingsEpic = (action$, state$) =>
  action$.pipe(
    ofType(actions.selectedMarker.type),
    filter((action) => action.payload?.type === MarkerType.PlanUnit.toString()),
    switchMap((action) => {
      // get all the markers from the state, then the north, south, east, and west coordinates
      // and finally all the visible markers that belong to either a plan detail or a spec detail.
      const allMarkers = mapMarkers(state$.value);
      const coordinates = geoCoordinates(state$.value);
      const homeMarkers = allMarkers.filter((pin) => pin.type !== MarkerType.BuildablePlan.toString());

      // get the screen size
      const layoutBreakpoint = selectLayoutBreakpoint(state$.value).getValue();

      // if larger devices, we will only display 1 card, otherwise we will display 7
      const pagination = layoutBreakpoint === LayoutBreakpoint.Small.getValue() ? 7 : 1;

      // get the nearby pins, sorted by distance from the marker.
      const nearbyPins = findNearbyPins(action.payload, homeMarkers, coordinates, pagination);

      // the backend expects a list of plan ids to fetch, so we grab just the IDs
      const planIds = nearbyPins.filter(pin => 
        pin.type !== MarkerType.SpecUnit.toString()).map((pin) => pin.id).join(','); 
      const specIds = nearbyPins.filter(pin => 
        pin.type === MarkerType.SpecUnit.toString()).map((pin) => pin.id).join(','); 

      // we need this for the sort order, since we get plans and specs separately, we need to merge them in the
      // previously sorted order.
      const sortOrder = nearbyPins.map((pin) => pin.id).join(',');

      return getPlanNearbyListings({
        planIds,
        specIds,
        sortOrder
      }).pipe(
        switchMap((data) => of(actions.getNearbyListingsCarouselFulfilled(data))),
        catchError((error) => of(actions.getNearbyListingsCarouselFailed(error)))
      )
    })
  );

/**
 * Epic for retrieving additional marker information for a spec.
 *
 * @param {Observable} action$ - The stream of actions.
 * @returns {Observable} The stream of actions for retrieving additional marker information.
 */
export const getSpecNearbyListingsEpic = (action$, state$) =>
  action$.pipe(
    ofType(actions.selectedMarker.type),
    filter((action) => action.payload?.type === MarkerType.SpecUnit.toString()),
    switchMap((action) => {
      // get all the markers from the state, then the north, south, east, and west coordinates
      // and finally all the visible markers that belong to either a plan detail or a spec detail.
      const allMarkers = mapMarkers(state$.value);
      const coordinates = geoCoordinates(state$.value);
      const homeMarkers = allMarkers.filter((pin) => pin.type !== MarkerType.BuildablePlan.toString());
      
      // get the screen size
      const layoutBreakpoint = selectLayoutBreakpoint(state$.value).getValue();

      // if larger devices, we will only display 1 card, otherwise we will display 7
      const pagination = layoutBreakpoint === LayoutBreakpoint.Small.getValue() ? 7 : 1;
      const nearbyPins = findNearbyPins(action.payload, homeMarkers, coordinates, pagination);
      
      // the backend expects separate lists of plans and spec details, so we grab just the IDs
      const planIds = nearbyPins.filter(pin => 
        pin.type === MarkerType.PlanUnit.toString()).map((pin) => pin.id).join(','); 
      const specIds = nearbyPins.filter(pin => 
        pin.type !== MarkerType.PlanUnit.toString()).map((pin) => pin.id).join(','); 

      // we need this for the sort order, since we get plans and specs separately, we need to merge them in the
      // previously sorted order.
      const sortOrder = nearbyPins.map((pin) => pin.id).join(',');

      return getSpecNearbyListings({
        specIds,
        planIds,
        sortOrder
      }).pipe(
        switchMap((data) => of(actions.getNearbyListingsCarouselFulfilled(data))),
        catchError((error) => of(actions.getNearbyListingsCarouselFailed(error)))
      )
    })
  );

  /**
   * Clears the summary card information when a listing is focused.
   *
   * @param {Observable} action$ - The stream of actions.
   * @returns {Observable} The stream of actions to be dispatched.
   */
  export const clearNearbyListingsCarouselEpic = (action$, state$) =>
  action$.pipe(
    ofType(actions.focusedListingInformation.type, searchParametersActions.fetchResultsType.type),
    map(selectedNearbyListingsCarouselStatus),
    filter((status) => {
      const summaryCard = selectedNearbyListingCarousel(state$.value);

      // check that the card exists
      return Boolean(summaryCard?.Id) &&
      status !== LoadingStatus.Idle.toString()
    }),
    switchMap(() => of(actions.clearNearbyListingsCarousel()))
  )

  /**
   * Removes the focused listing information when the carousel of nearby results closes.
   * 
   * @param {Observable} action$ - The stream of actions.
   * @returns {Observable} The stream of actions to be dispatched.
   */
  export const clearFocusedListingOnNearbyCarouselClosedEpic = (action$) =>
    action$.pipe(
      ofType(
        actions.clearNearbyListingsCarousel.type,
      ),
      switchMap(() => of(actions.focusedListingInformation(null)))
    );

/**
 * fetchSummaryCardForInteractiveSitePlanEpic epic is meant to
 * retrieve a summary card after an interactive site plan
 * lot is selected by the user.
 * @returns {Observable} The stream of actions to be dispatched.
 */
export const fetchSummaryCardForInteractiveSitePlanEpic = (action$, state$) =>
  action$.pipe(
    ofType(actions.setLotInformation.type),
    map((action) => ({
      communityId: action.payload?.communityId,
      specId: action.payload?.availableSpec,
    })),
    distinctUntilChanged((previous, current) => {
      if (!selectedLotSummaryCard(state$.value)) {
        return false;
      }

      return previous.communityId === current.communityId &&
        previous.specId === current.specId;
    }),
    filter(({ communityId, specId }) => Boolean(communityId) || Boolean(specId)),
    switchMap(({ communityId, specId }) => {
      const getSummaryCard$ = specId
        ? getHousingUnitSummaryCardInformation(specId)
        : getCommunitySummaryCard(communityId);

      return getSummaryCard$.pipe(
        switchMap((data) => of(actions.getInteractiveSitePlanCardFulfilled(data))),
        catchError((error) => of(actions.getInteractiveSitePlanCardFailed(error)))
      );
    })
  );


/**
 * removeInteractiveSitePlanEpic will react to community maps removed in order to remove
 * the interactive site plan information related to them, if any.
 * @param {Observable} action$ - The stream of actions.
 * @returns {Observable} The stream of actions to be dispatched.
 */
export const removeInteractiveSitePlanEpic = (action$) =>
  action$.pipe(
    ofType(actions.removeCommunityMap.type),
    map((action) => actions.removeInteractiveSitePlan(action.payload))
  );